/**
 * ContainerPanel — Pulsar container ops block at the top of the LAB
 * (PULSE-202). Makes a dead detector VISIBLE instead of a silent "0
 * signals". Admin-only (the endpoint 403s for non-admins → panel hides).
 *
 * The whole point: a LIVE container that's down pulses RED (alarm); a
 * RETIRED container that's down is GREY (expected — the cvd-div trap).
 *
 * Controls (start/restart/logs) only for `controllable` containers;
 * critical infra (bot/db/redis) shows status + logs but no restart
 * button. Restarting a retired container warns first.
 */
import { useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Play,
  RotateCw,
  ScrollText,
  Server,
} from "lucide-react";

import { Drawer } from "@/components/ui/Drawer";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import {
  type LabContainer,
  useContainerAction,
  useContainerLogs,
  useLabContainers,
} from "@/hooks/useDashboardApi";
import { useAuthStore } from "@/stores/auth";
import { cn } from "@/lib/cn";
import { timeAgo } from "@/lib/format";

const GROUP_LABEL: Record<string, string> = {
  bot: "Бот",
  engine: "Движки",
  detector: "Детекторы",
  data: "Данные",
  support: "Сервис",
  infra: "Инфра",
  other: "Прочее",
};

type Pending = { name: string; action: "start" | "restart"; label: string; warn: boolean };

export function ContainerPanel() {
  const isAdmin = useAuthStore((s) => s.user?.role === "admin");
  const { data, isError } = useLabContainers(isAdmin);
  const action = useContainerAction();
  const [showAll, setShowAll] = useState(false);
  const [logsFor, setLogsFor] = useState<LabContainer | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);

  const items = data?.items ?? [];

  // live containers that aren't running = real problems (alarm).
  const downLive = useMemo(
    () => items.filter((c) => c.intent === "live" && c.status !== "running"),
    [items],
  );

  // Admin-only panel; hide for non-admins or on any error / empty load.
  if (!isAdmin || isError || (!data && items.length === 0)) return null;

  // Default view: only containers tied to ACTIVE strategies; "все" reveals rest.
  const relevant = items.filter((c) => c.relevant);
  const rest = items.filter((c) => !c.relevant);

  const doAction = (c: LabContainer, act: "start" | "restart") => {
    setPending({
      name: c.name,
      action: act,
      label: `${act === "start" ? "Запустить" : "Рестартнуть"} ${c.label}?`,
      warn: c.intent === "retired",
    });
  };

  return (
    <section className="bg-ink border border-line mb-4">
      <header className="flex items-center gap-2 px-4 py-2.5 border-b border-line">
        <Server size={13} className="text-textDim" />
        <h2 className="text-xs font-semibold uppercase tracking-wider text-textDim">
          Контейнеры
        </h2>
        {downLive.length > 0 ? (
          <span className="pill bg-rose-faint text-rose inline-flex items-center gap-1">
            <AlertTriangle size={11} /> {downLive.length} лежат
          </span>
        ) : (
          <span className="pill bg-pulse-faint text-pulse inline-flex items-center gap-1">
            <CheckCircle2 size={11} /> всё работает
          </span>
        )}
        <button
          onClick={() => setShowAll((v) => !v)}
          className="ml-auto text-2xs text-textDim hover:text-text inline-flex items-center gap-1"
        >
          {showAll ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          все ({items.length})
        </button>
      </header>

      {/* relevant (active-strategy) containers — compact grid */}
      <div className="p-2 grid gap-1.5 sm:grid-cols-2 xl:grid-cols-3">
        {relevant.map((c) => (
          <ContainerRow key={c.name} c={c} onAction={doAction} onLogs={setLogsFor} busy={action.isPending} />
        ))}
      </div>

      {/* the rest, grouped, behind the toggle */}
      {showAll && rest.length > 0 && (
        <div className="px-2 pb-2 border-t border-line/60 pt-2 space-y-2">
          {groupRest(rest).map(([g, list]) => (
            <div key={g}>
              <div className="text-2xs uppercase tracking-wider text-textFaint px-1 mb-1">
                {GROUP_LABEL[g] ?? g}
              </div>
              <div className="grid gap-1.5 sm:grid-cols-2 xl:grid-cols-3">
                {list.map((c) => (
                  <ContainerRow key={c.name} c={c} onAction={doAction} onLogs={setLogsFor} busy={action.isPending} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* logs drawer */}
      <LogsDrawer container={logsFor} onClose={() => setLogsFor(null)} />

      {/* control confirm */}
      <ConfirmDialog
        open={!!pending}
        title={pending?.label ?? ""}
        description={
          pending?.warn
            ? "Это РЕТАЙРНУТЫЙ контейнер — он остановлен намеренно (заменён/выключен). Рестарт может вернуть лишний источник сигналов. Точно?"
            : "Контейнер будет перезапущен. Кратковременный перерыв в его работе."
        }
        confirmLabel={pending?.action === "start" ? "Запустить" : "Рестартнуть"}
        variant={pending?.warn ? "danger" : "primary"}
        loading={action.isPending}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          if (!pending) return;
          action.mutate(
            { name: pending.name, action: pending.action },
            { onSettled: () => setPending(null) },
          );
        }}
      />
    </section>
  );
}

function groupRest(rest: LabContainer[]): [string, LabContainer[]][] {
  const order = ["engine", "detector", "data", "support", "infra", "other"];
  const by: Record<string, LabContainer[]> = {};
  for (const c of rest) (by[c.group] ??= []).push(c);
  return order.filter((g) => by[g]?.length).map((g) => [g, by[g]]);
}

/* ─── one container row ─────────────────────────────────────────────── */

function ContainerRow({
  c,
  onAction,
  onLogs,
  busy,
}: {
  c: LabContainer;
  onAction: (c: LabContainer, a: "start" | "restart") => void;
  onLogs: (c: LabContainer) => void;
  busy: boolean;
}) {
  const running = c.status === "running";
  const dot = dotClass(c);
  const when =
    running && validTs(c.started_at)
      ? `${timeAgo(c.started_at)}`
      : !running && validTs(c.finished_at)
        ? `лежит ${timeAgo(c.finished_at)}`
        : c.status;

  return (
    <div
      className={cn(
        "flex items-center gap-2 px-2.5 py-1.5 border border-line bg-surface/40 rounded-sm",
        c.intent === "retired" && "opacity-70",
      )}
    >
      <span className={cn("dot shrink-0", dot)} title={c.status} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-text truncate">{c.label}</span>
          {c.intent === "retired" && (
            <span className="pill bg-elevated text-textFaint shrink-0">ретайр</span>
          )}
          {c.signal && (
            <span className="text-2xs font-mono text-textFaint truncate hidden lg:inline">
              {c.signal}
            </span>
          )}
        </div>
        <div className="text-2xs text-textFaint font-mono truncate">{when}</div>
      </div>
      <div className="flex items-center gap-0.5 shrink-0">
        {c.controllable && !running && (
          <IconBtn title="Запустить" onClick={() => onAction(c, "start")} disabled={busy}>
            <Play size={12} className="text-pulse" />
          </IconBtn>
        )}
        {c.controllable && running && (
          <IconBtn title="Рестартнуть" onClick={() => onAction(c, "restart")} disabled={busy}>
            <RotateCw size={12} />
          </IconBtn>
        )}
        {c.status !== "absent" && (
          <IconBtn title="Логи" onClick={() => onLogs(c)} disabled={false}>
            <ScrollText size={12} />
          </IconBtn>
        )}
      </div>
    </div>
  );
}

function IconBtn({
  title,
  onClick,
  disabled,
  children,
}: {
  title: string;
  onClick: () => void;
  disabled: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className="w-6 h-6 grid place-items-center rounded-sm text-textDim hover:text-text hover:bg-elevated disabled:opacity-40 transition-colors"
    >
      {children}
    </button>
  );
}

/* ─── logs drawer ───────────────────────────────────────────────────── */

function LogsDrawer({ container, onClose }: { container: LabContainer | null; onClose: () => void }) {
  const { data, isLoading, isError } = useContainerLogs(container?.name ?? null, 300);
  return (
    <Drawer
      open={!!container}
      onClose={onClose}
      title={container ? `Логи · ${container.label}` : "Логи"}
      subtitle={container?.name}
      widthClassName="w-[760px]"
    >
      <div className="p-3">
        {isLoading && <div className="text-xs text-textFaint px-1 py-4">загрузка…</div>}
        {isError && <div className="text-xs text-rose px-1 py-4">не удалось получить логи</div>}
        {data && (
          <pre className="text-2xs leading-relaxed font-mono text-textDim whitespace-pre-wrap break-words bg-ink border border-line p-3 rounded-sm max-h-[calc(100vh-120px)] overflow-y-auto">
            {data.logs || "— пусто —"}
          </pre>
        )}
      </div>
    </Drawer>
  );
}

/* ─── helpers ───────────────────────────────────────────────────────── */

function dotClass(c: LabContainer): string {
  if (c.status === "running") {
    return c.health === "unhealthy" ? "bg-amber animate-pulse" : "bg-pulse";
  }
  // down: live → alarm (red pulse), retired → calm grey.
  if (c.status === "exited" || c.status === "absent") {
    return c.intent === "retired" ? "bg-textFaint" : "bg-rose animate-pulse";
  }
  return "bg-amber"; // created / restarting / paused
}

function validTs(ts: string | null): boolean {
  if (!ts) return false;
  // docker uses 0001-01-01T... for "never"
  return !ts.startsWith("0001-");
}
